import { SectionCard, Section } from "@blueprintjs/core";

function LearnPresident({ navigation }) {
    return (
        <Section
            className="page"
            title="The Role of the President and Veto Power"
            subtitle="How the President Influences the Legislative Process"
        >
            {navigation}
            <SectionCard className="learn-content">
                <p>
                    The President plays a pivotal role in the legislative
                    process by having the power to sign or veto bills that
                    Congress has passed. This final step in the legislative
                    process allows the President to approve or reject laws,
                    which can significantly impact the direction of the
                    country’s policy. This section explores the President's role
                    in the legislative process, focusing on the veto power and
                    the procedures involved when a bill is vetoed.
                </p>

                <h2>1. The President's Role in the Legislative Process</h2>
                <p>
                    After a bill passes both the House and Senate in identical
                    form, it is sent to the President for approval. The
                    President can either sign the bill into law, allowing it to
                    become part of the U.S. Code, or veto it, preventing it from
                    becoming law. The President's decision to sign or veto a
                    bill is an essential part of the system of checks and
                    balances, as it provides an additional layer of scrutiny
                    over the laws passed by Congress.
                </p>
                <p>
                    <ul>
                        <li>
                            <b>Signing the Bill into Law:</b> If the President
                            agrees with the bill and its provisions, they can
                            sign it into law. This action makes the bill an
                            official law, and it is enforced by the relevant
                            government agencies.
                        </li>
                        <li>
                            <b>Vetoing the Bill:</b> If the President disagrees
                            with the bill, they can veto it, returning it to
                            Congress with a written explanation of the reasons
                            for the veto. The vetoed bill does not become law
                            unless Congress overrides the veto with a
                            supermajority vote.
                        </li>
                    </ul>
                </p>

                <h2>2. Types of Vetoes</h2>
                <p>
                    There are two primary types of vetoes: the regular veto and
                    the pocket veto. Each serves different purposes and follows
                    different procedures.
                </p>
                <p>
                    <ul>
                        <li>
                            <b>Regular Veto:</b> This is the standard veto used
                            by the President when they reject a bill that has
                            passed both chambers of Congress. The President
                            sends a veto message to Congress, explaining the
                            reasons for the veto. The bill then returns to
                            Congress, where lawmakers may attempt to override
                            the veto.
                        </li>
                        <li>
                            <b>Pocket Veto:</b> If the President does not sign
                            or veto a bill within ten days (excluding Sundays)
                            while Congress is in session, the bill automatically
                            becomes law. However, if Congress adjourns during
                            this period, preventing the President from returning
                            the bill, the President can withhold action on the
                            bill, effectively “pocketing” it, and the bill does
                            not become law.
                        </li>
                    </ul>
                </p>

                <h2>3. The Veto Override Process</h2>
                <p>
                    If the President vetoes a bill, Congress has the option to
                    override the veto. This process requires a two-thirds
                    majority vote in both the House and Senate. If both chambers
                    successfully override the veto, the bill becomes law despite
                    the President's objections.
                </p>
                <p>
                    <ul>
                        <li>
                            <b>House of Representatives:</b> In the House, a
                            two-thirds vote is required to override the
                            President’s veto. This is often difficult to
                            achieve, especially when the President’s party holds
                            a significant majority in the House. However, if
                            there is broad bipartisan support for the bill, the
                            House may have the votes needed to override the
                            veto.
                        </li>
                        <li>
                            <b>Senate:</b> Similarly, the Senate must also pass
                            the veto override by a two-thirds majority. In the
                            Senate, where individual Senators have more
                            influence, it may be more difficult to secure enough
                            votes to override a veto. A successful veto override
                            in the Senate typically requires strong bipartisan
                            backing.
                        </li>
                    </ul>
                </p>

                <h2>4. The Impact of a Veto</h2>
                <p>
                    A vetoed bill is effectively blocked from becoming law, but
                    the veto itself does not end the legislative process. If
                    Congress fails to override the veto, the bill is rejected,
                    and lawmakers may choose to make changes to the bill and
                    attempt to pass a revised version. The veto can also
                    influence future legislation, as lawmakers may work to
                    address the President's concerns in subsequent bills.
                </p>
                <p>
                    <ul>
                        <li>
                            <b>Political Consequences:</b> A veto can also have
                            significant political consequences. It often sparks
                            public debate and may rally voters in favor of or
                            against the President’s position on the bill. The
                            veto process can also strengthen or weaken the
                            relationship between the executive and legislative
                            branches, depending on the level of bipartisan
                            support for the bill and the President's stance.
                        </li>
                    </ul>
                </p>

                <h2>5. The Importance of the President’s Veto Power</h2>
                <p>
                    The President’s veto power serves as a check on Congress's
                    legislative power, ensuring that laws passed by Congress are
                    in line with the President’s agenda and the country’s
                    broader interests. While the veto is not often used, it
                    plays an important role in maintaining the balance of power
                    between the branches of government. By requiring a
                    supermajority in Congress to override a veto, the
                    Constitution ensures that the President’s objections are not
                    easily overridden, allowing for more thoughtful
                    consideration of proposed laws.
                </p>
                <p>
                    <ul>
                        <li>
                            <b>Encouraging Compromise:</b> The veto power also
                            encourages compromise between the executive and
                            legislative branches. When the President vetoes a
                            bill, it signals to Congress that adjustments may be
                            needed for the bill to succeed. This can prompt
                            lawmakers to modify the bill to gain the President’s
                            support or to secure the necessary votes in Congress
                            to override the veto.
                        </li>
                    </ul>
                </p>

                <h2>6. The Final Step: Becoming Law</h2>
                <p>
                    Once a bill is signed by the President or if Congress
                    successfully overrides a veto, the bill becomes law. At this
                    point, it is enforced by the relevant government agencies,
                    and its provisions become part of the U.S. Code. The bill’s
                    journey from introduction to law is now complete, but its
                    impact will continue as it is implemented and interpreted by
                    the courts and government agencies.
                </p>
            </SectionCard>
        </Section>
    );
}

export default LearnPresident;
